'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, X } from 'lucide-react';
import type { WorkloadMetricsInterval } from '@/lib/urm-api';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { buildGroupedSeries, filterWorkloadIntervalsByClients, listWorkloadClients } from '../logic';
import { GROUPING_DIMENSIONS, type ChartMetric, type ChartPairConfig, type GroupingDimension } from '../types';
import { UserMultiSelect } from './UserMultiSelect';
import { UserTimeChart } from './UserTimeChart';

function ChartCard({
  title,
  subtitle,
  onRemove,
  children,
}: {
  title: string;
  subtitle: string;
  onRemove?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="min-w-0 rounded-lg border bg-card px-3 pt-3 pb-1 shadow-sm">
      <div className="mb-2 flex min-h-7 items-start justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            title={`Remove ${title} chart`}
            aria-label={`Remove ${title} chart`}
            className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

export function WorkloadChartPair({
  config,
  intervals,
  loading,
  canRemove,
  onChange,
  onRemove,
  onAdd,
  onAddForClient,
  onHiddenSeriesChange,
  initialHiddenSeries = [],
}: {
  config: ChartPairConfig;
  intervals: WorkloadMetricsInterval[];
  loading: boolean;
  canRemove: boolean;
  onChange: (config: ChartPairConfig) => void;
  onRemove: () => void;
  onAdd?: () => void;
  onAddForClient?: (client: string) => void;
  onHiddenSeriesChange?: (id: number, hiddenSeries: string[]) => void;
  initialHiddenSeries?: string[];
}) {
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(
    () => new Set(initialHiddenSeries),
  );
  const clientsInRange = useMemo(() => listWorkloadClients(intervals), [intervals]);
  const filteredIntervals = useMemo(
    () => filterWorkloadIntervalsByClients(intervals, config.clients),
    [config.clients, intervals],
  );
  const series = useMemo(
    () => buildGroupedSeries(filteredIntervals, config.dimensions),
    [filteredIntervals, config.dimensions],
  );
  useEffect(() => {
    onHiddenSeriesChange?.(config.id, Array.from(hiddenSeries));
  }, [config.id, hiddenSeries, onHiddenSeriesChange]);
  const updateClients = (clients: string[] | null) => {
    setHiddenSeries(new Set());
    onChange({
      ...config,
      clients,
      dimensions: clients !== null && clients.length > 1
        ? config.dimensions.filter((dimension) => dimension !== 'username')
        : config.dimensions,
    });
  };
  const toggleDimension = (dimension: GroupingDimension) => {
    const selected = new Set(config.dimensions);
    if (selected.has(dimension)) selected.delete(dimension);
    else selected.add(dimension);
    setHiddenSeries(new Set());
    onChange({
      ...config,
      dimensions: GROUPING_DIMENSIONS
        .map((item) => item.key)
        .filter((key) => selected.has(key)),
    });
  };
  const toggleSeries = (name: string) => {
    setHiddenSeries((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };
  const isolateSeries = (name: string) => {
    setHiddenSeries((current) => {
      const names = series.map((item) => item.label);
      const onlyThisVisible = current.size === names.length - 1 && !current.has(name);
      return onlyThisVisible ? new Set() : new Set(names.filter((candidate) => candidate !== name));
    });
  };
  const removeMetric = (metric: ChartMetric) => {
    if (config.metrics.length === 1) return;
    onChange({ ...config, metrics: config.metrics.filter((candidate) => candidate !== metric) });
  };
  const canRemoveMetric = config.metrics.length > 1;
  const selectedUsersDescription = config.clients === null
    ? 'this aggregation'
    : config.clients.length === 1
      ? config.clients[0] || 'Unknown'
      : `${config.clients.length} selected users`;

  return (
    <section className="space-y-2 rounded-xl border border-primary/20 bg-muted/40 p-2.5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span>Users</span>
              <UserMultiSelect
                label="Workload chart users"
                users={clientsInRange}
                selectedUsers={config.clients}
                onChange={updateClients}
                allowAll
                allLabel="All users (Top 15)"
              />
            </div>
            <div className="flex flex-wrap items-center gap-1">
              <span className="mr-1 text-xs text-muted-foreground">Group by</span>
              {GROUPING_DIMENSIONS.map((dimension) => {
                const selected = config.dimensions.includes(dimension.key);
                return (
                  <button
                    key={dimension.key}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleDimension(dimension.key)}
                    className={cn(
                      'inline-flex h-6 items-center rounded-md border px-2.5 text-xs transition-colors',
                      selected
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'bg-background text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {dimension.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onAdd && (
            <Button type="button" variant="outline" size="sm" onClick={onAdd}>
              <Plus className="h-4 w-4" /> Add chart group
            </Button>
          )}
          {canRemove && (
            <Button type="button" variant="ghost" size="icon" onClick={onRemove} title="Remove chart group">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <div className={cn(
        'grid min-w-0 grid-cols-1 gap-2',
        config.metrics.length >= 2 && 'xl:grid-cols-2',
        config.metrics.length === 3 && '2xl:grid-cols-3',
      )}>
        {config.metrics.includes('processingLoad') && <ChartCard
          title="Concurrency"
          subtitle={`Top 15 groups for ${selectedUsersDescription}`}
          onRemove={canRemoveMetric ? () => removeMetric('processingLoad') : undefined}
        >
          <UserTimeChart
            series={series}
            metric="processingLoad"
            hiddenSeries={hiddenSeries}
            onToggleSeries={toggleSeries}
            onIsolateSeries={isolateSeries}
            onAddClientGroup={onAddForClient}
            loading={loading}
          />
        </ChartCard>}
        {config.metrics.includes('requestRate') && <ChartCard
          title="Requests per second"
          subtitle={`The same groups for ${selectedUsersDescription}`}
          onRemove={canRemoveMetric ? () => removeMetric('requestRate') : undefined}
        >
          <UserTimeChart
            series={series}
            metric="requestRate"
            hiddenSeries={hiddenSeries}
            onToggleSeries={toggleSeries}
            onIsolateSeries={isolateSeries}
            onAddClientGroup={onAddForClient}
            loading={loading}
          />
        </ChartCard>}
        {config.metrics.includes('averageDurationMs') && <ChartCard
          title="Average request duration"
          subtitle="Mean wall-clock duration; percentiles are not collected"
          onRemove={canRemoveMetric ? () => removeMetric('averageDurationMs') : undefined}
        >
          <UserTimeChart
            series={series}
            metric="averageDurationMs"
            hiddenSeries={hiddenSeries}
            onToggleSeries={toggleSeries}
            onIsolateSeries={isolateSeries}
            onAddClientGroup={onAddForClient}
            loading={loading}
          />
        </ChartCard>}
      </div>
    </section>
  );
}
